import React from "react";
import {
  Box,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { useTranslation } from "react-i18next";
import { CustomTextField } from "../../ui/CustomTextField";

const buildAddressSummary = ({
  hospital,
  ward,
  building,
  street,
  zone,
  floor,
  zipcode,
  city,
  country,
  name,
  phone,
  email,
}) => {
  const line1 = [hospital, ward, building].filter(Boolean).join(", ");
  const line2 = [street, zone, floor, zipcode, city, country]
    .filter(Boolean)
    .join(", ");
  const contact = [name, phone, email].filter(Boolean).join(" · ");

  return {
    line1: line1 || "Address not completed",
    line2: line2 || "Location details not completed",
    contact: contact || "Contact not completed",
  };
};

const renderAddressFields = ({
  prefix,
  hospital,
  setHospital,
  ward,
  setWard,
  building,
  setBuilding,
  street,
  setStreet,
  zone,
  setZone,
  floor,
  setFloor,
  zipcode,
  setZipcode,
  city,
  country,
  fieldErrors,
}) => (
  <>
    <CustomTextField
      name={`${prefix}Hospital`}
      label="Hospital"
      value={hospital}
      onChange={(event) => setHospital(event.target.value)}
      error={!!fieldErrors.hospital}
      helperText={fieldErrors.hospital}
    />
    <CustomTextField
      name={`${prefix}Ward`}
      label="Ward"
      value={ward}
      required={false}
      onChange={(event) => setWard(event.target.value)}
    />
    <CustomTextField
      name={`${prefix}Building`}
      label="Building"
      value={building}
      required={false}
      onChange={(event) => setBuilding(event.target.value)}
    />
    <CustomTextField
      name={`${prefix}Street`}
      label="Street"
      value={street}
      onChange={(event) => setStreet(event.target.value)}
      error={!!fieldErrors.street}
      helperText={fieldErrors.street}
    />
    <CustomTextField
      name={`${prefix}Zone`}
      label="Zone"
      value={zone}
      required={false}
      onChange={(event) => setZone(event.target.value)}
    />
    <CustomTextField
      name={`${prefix}Floor`}
      label="Floor"
      value={floor}
      required={false}
      onChange={(event) => setFloor(event.target.value)}
    />
    <CustomTextField
      name={`${prefix}Zipcode`}
      label="Zip Code"
      value={zipcode}
      onChange={(event) => setZipcode(event.target.value)}
      error={!!fieldErrors.zipcode}
      helperText={fieldErrors.zipcode}
    />
    <CustomTextField
      name={`${prefix}City`}
      label="City"
      value={city}
      readOnly
      InputLabelProps={{ shrink: true }}
    />
    <CustomTextField
      name={`${prefix}Country`}
      label="Country"
      value={country}
      readOnly
      InputLabelProps={{ shrink: true }}
    />
  </>
);

const AddressForm = ({
  editable = true,
  showDestination = true,
  hospital,
  setHospital,
  ward,
  setWard,
  building,
  setBuilding,
  street,
  setStreet,
  floor,
  setFloor,
  zone,
  setZone,
  zipcode,
  setZipcode,
  phone,
  setPhone,
  name,
  setName,
  email,
  setEmail,
  destinationHospital,
  setDestinationHospital,
  destinationWard,
  setDestinationWard,
  destinationBuilding,
  setDestinationBuilding,
  destinationStreet,
  setDestinationStreet,
  destinationZone,
  setDestinationZone,
  destinationFloor,
  setDestinationFloor,
  destinationZipcode,
  setDestinationZipcode,
  destinationCity,
  destinationCountry,
  destinationContactName,
  setDestinationContactName,
  destinationContactPhone,
  setDestinationContactPhone,
  destinationContactEmail,
  setDestinationContactEmail,
  destinationOptions = [],
  selectedDestinationId = "",
  onDestinationChange,
  fieldErrors,
  center,
}) => {
  const { t } = useTranslation();
  const [pickupExpanded, setPickupExpanded] = React.useState(false);
  const [destinationExpanded, setDestinationExpanded] = React.useState(false);
  const pickupSnapshotRef = React.useRef(null);
  const destinationSnapshotRef = React.useRef(null);
  const pickupSummary = buildAddressSummary({
    hospital,
    ward,
    building,
    street,
    zone,
    floor,
    zipcode,
    city: center?.city,
    country: center?.country,
    name,
    phone,
    email,
  });
  const destinationSummary = buildAddressSummary({
    hospital: destinationHospital,
    ward: destinationWard,
    building: destinationBuilding,
    street: destinationStreet,
    zone: destinationZone,
    floor: destinationFloor,
    zipcode: destinationZipcode,
    city: destinationCity,
    country: destinationCountry,
    name: destinationContactName,
    phone: destinationContactPhone,
    email: destinationContactEmail,
  });

  React.useEffect(() => {
    if (!editable) {
      pickupSnapshotRef.current = null;
      destinationSnapshotRef.current = null;
      setPickupExpanded(false);
      setDestinationExpanded(false);
    }
  }, [editable]);

  const openPickup = () => {
    if (!editable) return;

    pickupSnapshotRef.current = {
      hospital,
      ward,
      building,
      street,
      zone,
      floor,
      zipcode,
      name,
      phone,
      email,
    };
    setPickupExpanded(true);
  };

  const confirmPickup = () => {
    pickupSnapshotRef.current = null;
    setPickupExpanded(false);
  };

  const cancelPickup = () => {
    const snapshot = pickupSnapshotRef.current;

    if (snapshot) {
      setHospital(snapshot.hospital);
      setWard(snapshot.ward);
      setBuilding(snapshot.building);
      setStreet(snapshot.street);
      setZone(snapshot.zone);
      setFloor(snapshot.floor);
      setZipcode(snapshot.zipcode);
      setName(snapshot.name);
      setPhone(snapshot.phone);
      setEmail(snapshot.email);
    }

    pickupSnapshotRef.current = null;
    setPickupExpanded(false);
  };

  const openDestination = () => {
    if (!editable) return;

    destinationSnapshotRef.current = {
      destinationHospital,
      destinationWard,
      destinationBuilding,
      destinationStreet,
      destinationZone,
      destinationFloor,
      destinationZipcode,
      destinationContactName,
      destinationContactPhone,
      destinationContactEmail,
    };
    setDestinationExpanded(true);
  };

  const confirmDestination = () => {
    destinationSnapshotRef.current = null;
    setDestinationExpanded(false);
  };

  const cancelDestination = () => {
    const snapshot = destinationSnapshotRef.current;

    if (snapshot) {
      setDestinationHospital(snapshot.destinationHospital);
      setDestinationWard(snapshot.destinationWard);
      setDestinationBuilding(snapshot.destinationBuilding);
      setDestinationStreet(snapshot.destinationStreet);
      setDestinationZone(snapshot.destinationZone);
      setDestinationFloor(snapshot.destinationFloor);
      setDestinationZipcode(snapshot.destinationZipcode);
      setDestinationContactName?.(snapshot.destinationContactName);
      setDestinationContactPhone?.(snapshot.destinationContactPhone);
      setDestinationContactEmail?.(snapshot.destinationContactEmail);
    }

    destinationSnapshotRef.current = null;
    setDestinationExpanded(false);
  };

  return (
    <>
      <Grid item xs={12}>
        <Box
          sx={{
            mt: 1.5,
            p: 2,
            borderRadius: 1,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "divider",
          }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Pickup Address
            </Typography>
            {pickupExpanded && editable ? (
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <IconButton
                  size="small"
                  aria-label="Confirm pickup address"
                  onClick={confirmPickup}>
                  <CheckIcon fontSize="small" color="success" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="Cancel pickup address changes"
                  onClick={cancelPickup}>
                  <CloseIcon fontSize="small" color="error" />
                </IconButton>
              </Box>
            ) : editable ? (
              <IconButton
                size="small"
                aria-label="Edit pickup address"
                onClick={openPickup}>
                <EditIcon fontSize="small" color="primary" />
              </IconButton>
            ) : null}
          </Box>

          {!pickupExpanded && (
            <Box sx={{ mt: 1, color: "text.secondary" }}>
              <Typography variant="body2">{pickupSummary.line1}</Typography>
              <Typography variant="body2">{pickupSummary.line2}</Typography>
              <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600 }}>
                {pickupSummary.contact}
              </Typography>
            </Box>
          )}

          <Collapse in={pickupExpanded} timeout="auto" unmountOnExit={false}>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              {renderAddressFields({
                prefix: "pickup",
                hospital,
                setHospital,
                ward,
                setWard,
                building,
                setBuilding,
                street,
                setStreet,
                zone,
                setZone,
                floor,
                setFloor,
                zipcode,
                setZipcode,
                city: center?.city,
                country: center?.country,
                fieldErrors,
              })}

              <Grid item xs={12}>
                <Box sx={{ mt: 1.5, mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Pickup Contact
                  </Typography>
                </Box>
              </Grid>
              <CustomTextField
                name="recipientName"
                label="Name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                error={!!fieldErrors.name}
                helperText={fieldErrors.name}
              />
              <CustomTextField
                name="phone"
                label={t("phoneNumber")}
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                error={!!fieldErrors.phone}
                helperText={fieldErrors.phone}
              />
              <CustomTextField
                name="email"
                label={t("email")}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                error={!!fieldErrors.email}
                helperText={fieldErrors.email}
              />
            </Grid>
          </Collapse>
        </Box>
      </Grid>

      {showDestination && (
        <>
          <Grid item xs={12}>
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 1,
                bgcolor: "grey.50",
                border: "1px solid",
                borderColor: "divider",
              }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: { xs: "wrap", sm: "nowrap" },
                }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Destination Address
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: 1,
                    flex: 1,
                    ml: 2,
                  }}>
                  {destinationOptions.length > 0 && (
                    <FormControl
                      size="small"
                      sx={{ minWidth: { xs: 150, sm: 240 } }}>
                      <InputLabel id="sample-destination-select-label">
                        {t("destination", "Destination")}
                      </InputLabel>
                      <Select
                        labelId="sample-destination-select-label"
                        value={selectedDestinationId}
                        label={t("destination", "Destination")}
                        disabled={!editable || destinationExpanded}
                        onChange={(event) =>
                          onDestinationChange?.(event.target.value)
                        }>
                        <MenuItem value="" disabled>
                          {t("customAddress", "Custom address")}
                        </MenuItem>
                        {destinationOptions.map((destination) => (
                          <MenuItem key={destination.id} value={destination.id}>
                            {destination.title}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  {destinationExpanded && editable ? (
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <IconButton
                        size="small"
                        aria-label="Confirm destination address"
                        onClick={confirmDestination}>
                        <CheckIcon fontSize="small" color="success" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label="Cancel destination address changes"
                        onClick={cancelDestination}>
                        <CloseIcon fontSize="small" color="error" />
                      </IconButton>
                    </Box>
                  ) : editable ? (
                    <IconButton
                      size="small"
                      aria-label="Edit destination address"
                      onClick={openDestination}>
                      <EditIcon fontSize="small" color="primary" />
                    </IconButton>
                  ) : null}
                </Box>
              </Box>

              {!destinationExpanded && (
                <Box sx={{ mt: 1, color: "text.secondary" }}>
                  <Typography variant="body2">
                    {destinationSummary.line1}
                  </Typography>
                  <Typography variant="body2">
                    {destinationSummary.line2}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600 }}>
                    {destinationSummary.contact}
                  </Typography>
                </Box>
              )}

              <Collapse
                in={destinationExpanded}
                timeout="auto"
                unmountOnExit={false}>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  {renderAddressFields({
                    prefix: "destination",
                    hospital: destinationHospital,
                    setHospital: setDestinationHospital,
                    ward: destinationWard,
                    setWard: setDestinationWard,
                    building: destinationBuilding,
                    setBuilding: setDestinationBuilding,
                    street: destinationStreet,
                    setStreet: setDestinationStreet,
                    zone: destinationZone,
                    setZone: setDestinationZone,
                    floor: destinationFloor,
                    setFloor: setDestinationFloor,
                    zipcode: destinationZipcode,
                    setZipcode: setDestinationZipcode,
                    city: destinationCity,
                    country: destinationCountry,
                    fieldErrors,
                  })}

                  <Grid item xs={12}>
                    <Box sx={{ mt: 1.5, mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Destination Contact
                      </Typography>
                    </Box>
                  </Grid>
                  <CustomTextField
                    name="destinationContactName"
                    label="Name"
                    value={destinationContactName || ""}
                    onChange={(event) =>
                      setDestinationContactName?.(event.target.value)
                    }
                    error={!!fieldErrors.destinationContactName}
                    helperText={fieldErrors.destinationContactName}
                  />
                  <CustomTextField
                    name="destinationContactPhone"
                    label={t("phoneNumber")}
                    value={destinationContactPhone || ""}
                    onChange={(event) =>
                      setDestinationContactPhone?.(event.target.value)
                    }
                    error={!!fieldErrors.destinationContactPhone}
                    helperText={fieldErrors.destinationContactPhone}
                  />
                  <CustomTextField
                    name="destinationContactEmail"
                    label={t("email")}
                    value={destinationContactEmail || ""}
                    onChange={(event) =>
                      setDestinationContactEmail?.(event.target.value)
                    }
                    error={!!fieldErrors.destinationContactEmail}
                    helperText={fieldErrors.destinationContactEmail}
                  />
                </Grid>
              </Collapse>
            </Box>
          </Grid>
        </>
      )}
    </>
  );
};

export default AddressForm;
